import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import { DELIVERY_ZONES } from "../data/deliveryZones";
import {
  initializeMobilePayment,
  initializeBankTransfer,
  verifyMobilePayment,
  verifyBankTransfer,
  pollPaymentConfirmation,
  generateChargeId,
  MOBILE_OPERATORS,
  PAYMENT_METHODS,
} from "../services/paychangu";
import { createOrder } from "../firebase/firestoreService";
import { useAuth } from "../context/AuthContext";
import firebase, { db } from "../firebase/config";
import "../styles/checkout.css";

const fmt = (n) => `MK ${Number(n).toLocaleString()}`;
const getItemImage = (item) =>
  item.selImage || item.image || item.images?.[0] || item.colors?.[0]?.image || "";

function useIsMobile() {
  const [mobile, setMobile] = useState(() => window.innerWidth <= 768);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 768px)");
    const h = (e) => setMobile(e.matches);
    mq.addEventListener("change", h);
    return () => mq.removeEventListener("change", h);
  }, []);
  return mobile;
}

// ─── Step Card ────────────────────────────────────────────────────────────────
function StepCard({ number, title, completed, summary, onOpen }) {
  return (
    <div className={`checkout__step-card ${completed ? "checkout__step-card--done" : ""}`}>
      <div className="checkout__step-card-left">
        <div className={`checkout__step-badge ${completed ? "checkout__step-badge--done" : ""}`}>
          {completed ? "✓" : number}
        </div>
        <div>
          <p className="checkout__step-title">{title}</p>
          {completed && summary && (
            <p className="checkout__step-summary">{summary}</p>
          )}
        </div>
      </div>
      <button className="checkout__step-btn" onClick={onOpen}>
        {completed ? "Edit" : "Set up →"}
      </button>
    </div>
  );
}

// ─── Popup Shell ──────────────────────────────────────────────────────────────
function Popup({ title, onClose, items, onProceed, buttonLabel = "Proceed →", children, inline }) {
  const deliveryDate = new Date();
  deliveryDate.setDate(deliveryDate.getDate() + 2);
  const deliveryStr = deliveryDate.toLocaleDateString("en-GB", {
    weekday: "short", day: "numeric", month: "short",
  });
  const total = items.reduce((s, i) => s + i.price * (i.qty || 1), 0);

  if (inline) {
    return (
      <div className="checkout-inline-form">
        <button className="checkout-inline-back" onClick={onClose}>← Back</button>
        <h3 className="checkout-inline-title">{title}</h3>
        {children}
        <button className="checkout-popup__confirm" onClick={onProceed}>
          {buttonLabel}
        </button>
      </div>
    );
  }

  return (
    <div className="checkout-popup-overlay" onClick={onClose}>
      <div className="checkout-popup" onClick={(e) => e.stopPropagation()}>
        <div className="checkout-popup__header">
          <h3 className="checkout-popup__title">{title}</h3>
          <button className="checkout-popup__close" onClick={onClose}>✕</button>
        </div>

        <div className="checkout-popup__summary">
          {items.map((item, i) => (
            <div key={i} className="checkout-popup__summary-row">
              <span className="checkout-popup__summary-name">{item.name}</span>
              <span className="checkout-popup__summary-price">{fmt(item.price)}</span>
            </div>
          ))}
          <div className="checkout-popup__summary-total">
            <span>Total</span>
            <strong>{fmt(total)}</strong>
          </div>
          <div className="checkout-popup__delivery-tag">
            📦 Expected delivery: <strong>{deliveryStr}</strong>
          </div>
        </div>

        <div className="checkout-popup__divider" />

        <div className="checkout-popup__body">
          {children}
          <button className="checkout-popup__confirm" onClick={onProceed}>
            {buttonLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Delivery Popup ───────────────────────────────────────────────────────────
function DeliveryPopup({ district, deliveryOption, subOption, customArea, onConfirm, onClose, items, inline }) {
  const [localDistrict,  setLocalDistrict]  = useState(district);
  const [localOption,    setLocalOption]    = useState(deliveryOption);
  const [localSubOption, setLocalSubOption] = useState(subOption);
  const [localCustom,    setLocalCustom]    = useState(customArea || "");
  const [error,          setError]          = useState("");

  const selectedZone    = DELIVERY_ZONES.find((z) => z.id === localDistrict);
  const selectedOptData = selectedZone?.options.find((o) => o.id === localOption);

  const handleProceed = () => {
    if (!localDistrict) { setError("Please select a district."); return; }
    if (!localOption)   { setError("Please select a delivery option."); return; }
    if (selectedOptData?.subOptions?.length && !localSubOption) {
      setError(localOption === "door" ? "Please select an area." : "Please select a collection point.");
      return;
    }
    if (localSubOption === "other" && !localCustom.trim()) {
      setError("Please specify your area."); return;
    }
    onConfirm(localDistrict, localOption, localSubOption || null, localSubOption === "other" ? localCustom.trim() : null);
  };

  return (
    <Popup title="Delivery Location" onClose={onClose} items={items} onProceed={handleProceed} inline={inline}>
      <div className="checkout__form-group">
        <label className="checkout__label">District</label>
        <select className="checkout__select" value={localDistrict}
          onChange={(e) => {
            const zone = DELIVERY_ZONES.find((z) => z.id === e.target.value);
            setLocalDistrict(e.target.value);
            setLocalOption(zone?.options.length === 1 ? zone.options[0].id : null);
            setLocalSubOption(null);
          }}>
          <option value="">Select your district</option>
          {DELIVERY_ZONES.map((z) => (
            <option key={z.id} value={z.id}>{z.name}</option>
          ))}
        </select>
      </div>

      {selectedZone && selectedZone.options.length > 1 && (
        <div className="checkout__form-group">
          <label className="checkout__label">Delivery option</label>
          <div className="checkout__delivery-options">
            {selectedZone.options.map((opt) => (
              <button key={opt.id}
                className={`checkout__delivery-pill ${localOption === opt.id ? "checkout__delivery-pill--active" : ""}`}
                onClick={() => { setLocalOption(opt.id); setLocalSubOption(null); }}>
                {opt.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {selectedZone && selectedZone.options.length === 1 && (
        <p style={{ fontSize: 12, color: "#7C5C4A" }}>
          ✓ {selectedZone.options[0].label}
        </p>
      )}

      {selectedOptData?.subOptions?.length > 0 && (
        <div className="checkout__form-group">
          <label className="checkout__label">
            {localOption === "door" ? "Select area" : "Select collection point"}
          </label>
          <select className="checkout__select" value={localSubOption || ""}
            onChange={(e) => { setLocalSubOption(e.target.value || null); setLocalCustom(""); }}>
            <option value="">Choose…</option>
            {selectedOptData.subOptions.map((sub) => (
              <option key={sub.id} value={sub.id}>
                {sub.label}{sub.fee > 0 ? ` — MK ${sub.fee.toLocaleString()} delivery fee` : " — Courier charge"}
              </option>
            ))}
          </select>
        </div>
      )}

      {localSubOption === "other" && (
        <div className="checkout__form-group">
          <label className="checkout__label">Specify your area</label>
          <input
            className="checkout__input"
            value={localCustom}
            onChange={(e) => setLocalCustom(e.target.value)}
            placeholder="e.g. Area 29, Kanengo, Kawale…"
            autoFocus
          />
        </div>
      )}

      {error && <p className="checkout-popup__error">{error}</p>}
    </Popup>
  );
}

// ─── Parcel Receiver Popup ────────────────────────────────────────────────────
function ReceiverPopup({ details, onConfirm, onClose, items, inline }) {
  const [draft, setDraft] = useState({ ...details });
  const [error, setError] = useState("");
  const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));

  const handleProceed = () => {
    if (!draft.firstName.trim()) { setError("Enter first name."); return; }
    if (!draft.lastName.trim())  { setError("Enter last name.");  return; }
    if (!draft.phone.trim())     { setError("Enter phone number."); return; }
    onConfirm(draft);
  };

  return (
    <Popup title="Parcel Receiver" onClose={onClose} items={items} onProceed={handleProceed} inline={inline}>
      <div className="checkout__form-row">
        <div className="checkout__form-group">
          <label className="checkout__label">First Name</label>
          <input className="checkout__input" value={draft.firstName}
            onChange={(e) => set("firstName", e.target.value)} placeholder="First name" autoFocus />
        </div>
        <div className="checkout__form-group">
          <label className="checkout__label">Last Name</label>
          <input className="checkout__input" value={draft.lastName}
            onChange={(e) => set("lastName", e.target.value)} placeholder="Last name" />
        </div>
      </div>
      <div className="checkout__form-group">
        <label className="checkout__label">Phone Number</label>
        <input className="checkout__input" value={draft.phone}
          onChange={(e) => set("phone", e.target.value)} placeholder="e.g. 0991234567" />
      </div>
      {error && <p className="checkout-popup__error">{error}</p>}
    </Popup>
  );
}

// ─── Payment Popup ────────────────────────────────────────────────────────────
function PaymentPopup({ paymentMethod, operator, onConfirm, onClose, items, inline }) {
  const [localMethod, setLocalMethod] = useState(paymentMethod);
  const [localOp,     setLocalOp]     = useState(operator);
  const [error,       setError]       = useState("");

  const handleProceed = () => {
    if (!localMethod) { setError("Select a payment method."); return; }
    if (localMethod === "mobile_money" && !localOp) { setError("Select a network."); return; }
    onConfirm(localMethod, localOp);
  };

  return (
    <Popup title="Payment Method" onClose={onClose} items={items}
      onProceed={handleProceed} buttonLabel="Make Payment" inline={inline}>
      <label className="checkout__label" style={{ display: "block", marginBottom: 10 }}>How to pay</label>
      <div className="checkout__operator-options" style={{ marginBottom: 20 }}>
        {PAYMENT_METHODS.map((m) => (
          <button key={m.id}
            onClick={() => { setLocalMethod(m.id); setLocalOp(null); }}
            className={`checkout__operator-pill ${localMethod === m.id ? "checkout__operator-pill--active" : ""}`}>
            <span className={`checkout__operator-icon checkout__operator-icon--${m.id}`}>
              {m.id === "mobile_money" ? "📱" : "🏦"}
            </span>
            <span>
              <span style={{ display: "block", fontWeight: 500 }}>{m.label}</span>
              <span style={{ fontSize: 10, opacity: .7 }}>{m.sub}</span>
            </span>
            {localMethod === m.id && <span className="checkout__operator-check">✓</span>}
          </button>
        ))}
      </div>

      {localMethod === "mobile_money" && (
        <>
          <label className="checkout__label" style={{ display: "block", marginBottom: 10 }}>Network</label>
          <div className="checkout__operator-options" style={{ marginBottom: 16 }}>
            {MOBILE_OPERATORS.map((op) => (
              <button key={op.id}
                onClick={() => setLocalOp(op.id)}
                className={`checkout__operator-pill ${localOp === op.id ? "checkout__operator-pill--active" : ""}`}>
                <span className={`checkout__operator-icon checkout__operator-icon--${op.id}`}>
                  {op.id === "airtel" ? "A" : "T"}
                </span>
                {op.label}
                {localOp === op.id && <span className="checkout__operator-check">✓</span>}
              </button>
            ))}
          </div>
          {localOp && (
            <p className="checkout__operator-note">
              💡 You'll receive a payment prompt on your <strong>
                {MOBILE_OPERATORS.find(o => o.id === localOp)?.label}
              </strong> number.
            </p>
          )}
        </>
      )}

      {localMethod === "bank_transfer" && (
        <p className="checkout__operator-note">
          🏦 You'll receive a bank account number to transfer to after clicking Pay.
        </p>
      )}
      {error && <p className="checkout-popup__error">{error}</p>}
    </Popup>
  );
}

// ─── Bank Details Popup ───────────────────────────────────────────────────────
function BankDetailsPopup({ bankDetails, amount, onClose }) {
  const expiry = bankDetails.expiresAt
    ? new Date(bankDetails.expiresAt * 1000).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })
    : null;

  const rows = [
    { label: "Bank Name",      value: bankDetails.bankName },
    { label: "Account Number", value: bankDetails.accountNumber, mono: true },
    { label: "Account Name",   value: bankDetails.accountName },
    { label: "Phone Number",   value: "0888919178", mono: true },
    { label: "Amount",         value: fmt(amount), bold: true },
    ...(expiry ? [{ label: "Account Expires", value: `Today at ${expiry}`, warn: true }] : []),
  ];

  return (
    <div className="checkout-popup-overlay" onClick={onClose}>
      <div className="checkout-popup" onClick={(e) => e.stopPropagation()}>
        <div className="checkout-popup__header">
          <h3 className="checkout-popup__title">🏦 Transfer Payment Details</h3>
          <button className="checkout-popup__close" onClick={onClose}>✕</button>
        </div>
        <div className="checkout-popup__body">
          <p style={{ fontSize: 13, color: "#555", marginBottom: 16, lineHeight: 1.6 }}>
            Transfer exactly <strong>{fmt(amount)}</strong> to the account below.
            Your order confirms automatically once received.
          </p>

          {rows.map(({ label, value, mono, bold, warn }) => (
            <div key={label} style={{
              display: "flex", justifyContent: "space-between",
              padding: "10px 0", borderBottom: "1px solid #f5f2ee",
            }}>
              <span style={{ fontSize: 11, letterSpacing: "1px", textTransform: "uppercase", color: "#999" }}>
                {label}
              </span>
              <span style={{
                fontSize: 14,
                fontFamily: mono ? "monospace" : "inherit",
                fontWeight: bold ? 700 : 500,
                color: warn ? "#c0392b" : "#1a1a1a",
              }}>
                {value}
              </span>
            </div>
          ))}

          <p style={{ fontSize: 12, color: "#7C5C4A", marginTop: 14, lineHeight: 1.6,
            background: "#fdf3ec", borderRadius: 8, padding: "10px 14px" }}>
            ⏳ Waiting for your transfer… This page will update automatically once payment is received.
          </p>
        </div>
      </div>
    </div>
  );
}

// ─── Order Summary ────────────────────────────────────────────────────────────
function OrderSummary({ items, district, deliveryOption, subOption, paymentMethod, operator, onPay, canPay, paying, payStatus, phone, summaryRef }) {
  const subtotal     = items.reduce((sum, i) => sum + i.price * (i.qty || 1), 0);
  const selectedZone = DELIVERY_ZONES.find((z) => z.id === district);
  const selectedOpt  = selectedZone?.options.find((o) => o.id === deliveryOption);
  const selectedSubOpt = selectedOpt?.subOptions?.find((s) => s.id === subOption);
  const deliveryFee  = selectedSubOpt?.fee ?? 0;
  const total        = subtotal + deliveryFee;
  const selectedOp   = MOBILE_OPERATORS.find((o) => o.id === operator);

  const payLabel = () => {
    if (payStatus === "waiting" && paymentMethod === "mobile_money") return "⏳ Waiting for PIN…";
    if (payStatus === "waiting" && paymentMethod === "bank_transfer") return "⏳ Waiting for transfer…";
    if (paying) return "Initializing…";
    if (paymentMethod === "mobile_money" && selectedOp) return `Make Payment with ${selectedOp.label}`;
    if (paymentMethod === "bank_transfer") return "Make Payment";
    return "Make Payment";
  };

  return (
    <div className="checkout__summary" ref={summaryRef}>
      <h3 className="checkout__summary-title">Order Summary</h3>
      {items.map((item, i) => (
        <div key={i} className="checkout__summary-item">
          <img src={getItemImage(item)} alt={item.name} className="checkout__summary-thumb" />
          <div className="checkout__summary-info">
            <p className="checkout__summary-name">{item.name}</p>
            <p className="checkout__summary-meta">
              {item.selColor && `${item.selColor} · `}{item.selSize && `Size ${item.selSize}`}
            </p>
            <p className="checkout__summary-price">{fmt(item.price)}</p>
          </div>
        </div>
      ))}
      <hr className="checkout__summary-divider" />
      <div className="checkout__summary-row">
        <span>Subtotal</span><span>{fmt(subtotal)}</span>
      </div>
      {deliveryFee > 0 && (
        <div className="checkout__summary-row">
          <span>Delivery fee</span>
          <span>{fmt(deliveryFee)}</span>
        </div>
      )}
      <div className="checkout__summary-row">
        <span>Payment</span>
        <span>
          {paymentMethod === "bank_transfer" ? "Bank Transfer"
            : selectedOp ? selectedOp.label
            : <span style={{ color: "#bbb" }}>—</span>}
        </span>
      </div>
      <div className="checkout__summary-total">
        <span>Total</span><span>{fmt(total)}</span>
      </div>

      {canPay ? (
        <button className="checkout__pay-btn" onClick={onPay} disabled={paying}>
          {payLabel()}
        </button>
      ) : (
        <>
          <button className="checkout__pay-btn" disabled>
            Make Payment
          </button>
          <p className="checkout__pay-hint">Finish all steps to proceed</p>
        </>
      )}

      {payStatus === "waiting" && paymentMethod === "mobile_money" && (
        <p style={{ fontSize: 12, color: "#7C5C4A", textAlign: "center", marginTop: 10,
          background: "#fdf3ec", borderRadius: 8, padding: "10px 14px", lineHeight: 1.6 }}>
          📱 Prompt sent to <strong>{phone}</strong>.<br />Enter your PIN to confirm.
        </p>
      )}
      <p className="checkout__pay-note">Secured by Paychangu</p>
    </div>
  );
}

// ─── Main Checkout Page ───────────────────────────────────────────────────────
export default function Checkout({ cartItems = [], cartCount = 0, onCartClick, clearCart }) {
  const navigate    = useNavigate();
  const { user }    = useAuth();
  const isPhoneUser = user?.providerData?.[0]?.providerId === "password";
  const editModeRef = useRef(false);
  const isMobile    = useIsMobile();
  const summaryRef  = useRef(null);

  const [details,        setDetails]        = useState({ firstName: "", lastName: "", phone: "", email: "" });
  const [district,       setDistrict]       = useState("");
  const [deliveryOption, setDeliveryOption] = useState(null);
  const [subOption,      setSubOption]      = useState(null);
  const [customArea,     setCustomArea]     = useState(null);
  const [paymentMethod,  setPaymentMethod]  = useState(null);
  const [operator,       setOperator]       = useState(null);
  const [referredBy,     setReferredBy]     = useState(null);
  const [referralDoc,    setReferralDoc]    = useState(null);
  const [paying,         setPaying]         = useState(false);
  const [payStatus,      setPayStatus]      = useState("idle");
  const [error,          setError]          = useState("");
  const [bankDetails,    setBankDetails]    = useState(null);
  const [activeStep,     setActiveStep]     = useState("delivery"); // open delivery popup on mount

  useEffect(() => { window.scrollTo(0, 0); }, []);
  useEffect(() => { if (isMobile && activeStep) window.scrollTo(0, 0); }, [activeStep, isMobile]);

  // Pre-populate details and fetch referral info
  useEffect(() => {
    if (!user) return;
    if (!isPhoneUser) {
      const parts = (user.displayName || "").split(" ");
      setDetails({ firstName: parts[0] || "", lastName: parts.slice(1).join(" ") || "", email: user.email || "", phone: "" });
    }
    db.collection("users").doc(user.uid).get().then((doc) => {
      if (!doc.exists) return;
      const d = doc.data();
      if (isPhoneUser) {
        setDetails({ firstName: d.firstName || "", lastName: d.lastName || "", phone: d.phone || "", email: d.email || "" });
      }
      if (d.referredBy) {
        setReferredBy(d.referredBy);
        db.collection("referrals").where("code", "==", d.referredBy).get().then((snap) => {
          if (!snap.empty) setReferralDoc({ id: snap.docs[0].id, ...snap.docs[0].data() });
        });
      }
    });
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  const openStep = (step, editing = false) => {
    editModeRef.current = editing;
    setActiveStep(step);
  };

  const handleDeliveryConfirm = (dist, opt, subOpt, custom) => {
    setDistrict(dist);
    setDeliveryOption(opt);
    setSubOption(subOpt);
    setCustomArea(custom || null);
    setActiveStep(editModeRef.current ? null : "receiver");
  };

  const handleReceiverConfirm = (saved) => {
    setDetails(saved);
    setActiveStep(editModeRef.current ? null : "payment");
  };

  const handlePaymentConfirm = (method, op) => {
    setPaymentMethod(method);
    setOperator(op);
    setActiveStep(null);
    setTimeout(() => summaryRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
  };

  const canPay =
    details.firstName.trim() &&
    details.lastName.trim()  &&
    details.phone.trim()     &&
    district && deliveryOption && paymentMethod &&
    (paymentMethod === "bank_transfer" || operator);

  const buildOrderBase = (chargeId) => {
    const _zone    = DELIVERY_ZONES.find((z) => z.id === district);
    const _opt     = _zone?.options.find((o) => o.id === deliveryOption);
    const _subOpt  = _opt?.subOptions?.find((s) => s.id === subOption);
    const _op      = MOBILE_OPERATORS.find((o) => o.id === operator);
    const _fee     = _subOpt?.fee ?? 0;
    const subtotal = cartItems.reduce((s, i) => s + i.price * (i.qty || 1), 0);
    const total    = subtotal + _fee;
    const commissionAmount = referralDoc
      ? Math.round(total * referralDoc.commissionRate / 100)
      : 0;
    return {
      chargeId,
      userId:          user?.uid || null,
      customerName:    `${details.firstName} ${details.lastName}`,
      customerEmail:   details.email,
      customerPhone:   details.phone,
      paymentMethod,
      paymentOperator: paymentMethod === "mobile_money" ? (_op?.label || operator) : "Bank Transfer",
      district, deliveryOption, subOption, customArea,
      deliverySummary: `${_zone?.name} — ${_opt?.label}${_subOpt ? ` · ${_subOpt?.id === "other" && customArea ? customArea : _subOpt.label}` : ""}`,
      deliveryFee: _fee,
      items: cartItems,
      total,
      ...(referredBy ? { referralCode: referredBy, commissionAmount } : {}),
    };
  };

  const creditReferral = (commissionAmount) => {
    if (!referralDoc || !commissionAmount) return;
    db.collection("referrals").doc(referralDoc.id).update({
      totalEarnings: firebase.firestore.FieldValue.increment(commissionAmount),
      totalOrders:   firebase.firestore.FieldValue.increment(1),
      updatedAt:     firebase.firestore.FieldValue.serverTimestamp(),
    });
  };

  const handlePay = async () => {
    if (!canPay) return;
    setError("");
    setPaying(true);
    setPayStatus("idle");
    setBankDetails(null);

    const chargeId   = generateChargeId();
    const amount     = cartItems.reduce((s, i) => s + i.price * (i.qty || 1), 0) + deliveryFee;
    const selectedOp = MOBILE_OPERATORS.find((o) => o.id === operator);

    const orderData = buildOrderBase(chargeId);

    if (paymentMethod === "mobile_money") {
      const result = await initializeMobilePayment({
        firstName: details.firstName, lastName: details.lastName,
        email: details.email, phone: details.phone, amount, chargeId, operator,
      });
      if (!result.success) { setError(typeof result.error === "string" ? result.error : JSON.stringify(result.error)); setPaying(false); return; }

      setPayStatus("waiting");
      const poll = await pollPaymentConfirmation(chargeId, verifyMobilePayment, { timeoutMs: 120000 });
      if (!poll.confirmed) {
        setPaying(false);
        setPayStatus(poll.reason === "timeout" ? "timeout" : "failed");
        setError(poll.reason === "timeout"
          ? "Payment timed out. Please try again."
          : "Payment was declined or cancelled. Please try again.");
        return;
      }
      await createOrder({ ...orderData, status: "Confirmed", paychanguRef: result.data?.data?.ref_id || "" });
      creditReferral(orderData.commissionAmount);
      setPaying(false);
      const orderPayload1 = { chargeId, ...details, operator: selectedOp?.label, deliverySummary: orderData.deliverySummary, amount };
      sessionStorage.setItem("gvs_last_order", JSON.stringify(orderPayload1));
      navigate("/order-success", { state: { order: orderPayload1 } });
      if (clearCart) clearCart();
    }

    if (paymentMethod === "bank_transfer") {
      const result = await initializeBankTransfer({ amount, chargeId });
      if (!result.success) { setError(typeof result.error === "string" ? result.error : JSON.stringify(result.error)); setPaying(false); return; }

      const { success: _s, raw: _r, ...cleanResult } = result;
      setBankDetails({ ...cleanResult, chargeId });
      setPayStatus("waiting");
      setPaying(false);

      const poll = await pollPaymentConfirmation(chargeId, verifyBankTransfer, { timeoutMs: 600000 });
      if (poll.confirmed) {
        await createOrder({ ...orderData, status: "Confirmed", paychanguRef: chargeId });
        creditReferral(orderData.commissionAmount);
        const orderPayload2 = { chargeId, ...details, operator: "Bank Transfer", deliverySummary: orderData.deliverySummary, amount };
        sessionStorage.setItem("gvs_last_order", JSON.stringify(orderPayload2));
        navigate("/order-success", { state: { order: orderPayload2 } });
        if (clearCart) clearCart();
      } else {
        setPayStatus(poll.reason === "timeout" ? "timeout" : "failed");
        setError("Transfer not received within the time limit. Contact us with your reference: " + chargeId);
      }
    }
  };


  // Summaries for step cards
  const selectedZone   = DELIVERY_ZONES.find((z) => z.id === district);
  const selectedOpt    = selectedZone?.options.find((o) => o.id === deliveryOption);
  const selectedSubOpt = selectedOpt?.subOptions?.find((s) => s.id === subOption);
  const selectedOp     = MOBILE_OPERATORS.find((o) => o.id === operator);
  const deliveryFee    = selectedSubOpt?.fee ?? 0;

  const subOptLabel = selectedSubOpt?.id === "other" && customArea ? customArea : selectedSubOpt?.label;
  const deliverySummary = district && deliveryOption
    ? `${selectedZone?.name} — ${selectedOpt?.label}${subOptLabel ? ` · ${subOptLabel}` : ""}`
    : null;
  const receiverSummary = details.firstName ? `${details.firstName} ${details.lastName} · ${details.phone}` : null;
  const paymentSummary  = paymentMethod === "bank_transfer" ? "Bank Transfer" : selectedOp?.label || null;

  return (
    <div className="checkout">
      <Navbar cartCount={cartCount} onCartClick={onCartClick} />

      <div className="checkout__main">
        <OrderSummary
          items={cartItems} district={district} deliveryOption={deliveryOption} subOption={subOption}
          paymentMethod={paymentMethod} operator={operator}
          onPay={handlePay} canPay={canPay}
          paying={paying} payStatus={payStatus} phone={details.phone}
          summaryRef={summaryRef}
        />

        <div>
          <h1 className="checkout__title">Checkout</h1>

          {/* Step cards — hidden on mobile when a form is shown inline */}
          {!(isMobile && activeStep) && (
            <>
              <StepCard number={1} title="Delivery Location"
                completed={!!deliveryOption} summary={deliverySummary}
                onOpen={() => openStep("delivery", !!deliveryOption)} />

              <StepCard number={2} title="Parcel Receiver"
                completed={!!receiverSummary} summary={receiverSummary}
                onOpen={() => openStep("receiver", !!receiverSummary)} />

              <StepCard number={3} title="Payment Method"
                completed={!!paymentSummary} summary={paymentSummary}
                onOpen={() => openStep("payment", !!paymentSummary)} />

              {error && (
                <div style={{
                  background: "#fde8e8", border: "1px solid #f5c6c6",
                  borderRadius: 8, padding: "12px 16px", fontSize: 13,
                  color: "#c0392b", marginTop: 8,
                }}>
                  ⚠️ {error}
                </div>
              )}
            </>
          )}

          {/* Inline forms on mobile */}
          {isMobile && activeStep === "delivery" && (
            <DeliveryPopup inline
              district={district} deliveryOption={deliveryOption} subOption={subOption} customArea={customArea} items={cartItems}
              onConfirm={handleDeliveryConfirm} onClose={() => setActiveStep(null)}
            />
          )}
          {isMobile && activeStep === "receiver" && (
            <ReceiverPopup inline
              details={details} items={cartItems}
              onConfirm={handleReceiverConfirm} onClose={() => setActiveStep(null)}
            />
          )}
          {isMobile && activeStep === "payment" && (
            <PaymentPopup inline
              paymentMethod={paymentMethod} operator={operator} items={cartItems}
              onConfirm={handlePaymentConfirm} onClose={() => setActiveStep(null)}
            />
          )}
        </div>
      </div>

      {/* Desktop-only overlays */}
      {!isMobile && activeStep === "delivery" && (
        <DeliveryPopup
          district={district} deliveryOption={deliveryOption} subOption={subOption} customArea={customArea} items={cartItems}
          onConfirm={handleDeliveryConfirm} onClose={() => setActiveStep(null)}
        />
      )}
      {!isMobile && activeStep === "receiver" && (
        <ReceiverPopup
          details={details} items={cartItems}
          onConfirm={handleReceiverConfirm} onClose={() => setActiveStep(null)}
        />
      )}
      {!isMobile && activeStep === "payment" && (
        <PaymentPopup
          paymentMethod={paymentMethod} operator={operator} items={cartItems}
          onConfirm={handlePaymentConfirm} onClose={() => setActiveStep(null)}
        />
      )}
      {bankDetails && (
        <BankDetailsPopup
          bankDetails={bankDetails}
          amount={cartItems.reduce((s, i) => s + i.price * (i.qty || 1), 0)}
          onClose={() => setBankDetails(null)}
        />
      )}
    </div>
  );
}
